import { EyeOutlined } from "@ant-design/icons"
import { Button, Drawer, Empty, Flex, Skeleton, Space } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type ValidateErrorEntity } from "rc-field-form/es/interface"
import React, { useState } from "react"

import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { type DocumentRequestData } from "../../types/document.types.ts"
import { type TemplateDocumentFormData } from "../../types/template-document-form-data.tsx"

interface TemplateDocumentPreviewProps {
  saving: boolean
  form: FormInstance<TemplateDocumentFormData>
  leadId?: string
  customerId?: string
  templateId?: string
  fetchDocumentPreview: (data: DocumentRequestData) => Promise<string>
  alreadyHasFile?: boolean
  submitLabel?: string
  onSubmitClick: () => Promise<void>
  onGenerateClick: () => Promise<void>
  onClickSendToApproval?: () => Promise<void>
}

export function TemplateDocumentPreview({
  saving,
  form,
  leadId,
  templateId,
  customerId,
  fetchDocumentPreview,
  alreadyHasFile,
  submitLabel = "Submit",
  onGenerateClick,
  onSubmitClick,
  onClickSendToApproval
}: TemplateDocumentPreviewProps) {
  const { normalizeDate } = useDateFormat()
  const [open, setOpen] = useState(false)
  const [HTML, setHTML] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [action, setAction] = useState("")
  const [error, setError] = useState(false)

  const onCancelClick = () => {
    setOpen(false)
    setHTML(null)
  }

  const fetchDocumentPreviewHandler = async (
    values: TemplateDocumentFormData
  ) => {
    try {
      setLoading(true)
      const resHtml = await fetchDocumentPreview({
        lead_id: leadId,
        customer_id: templateId,
        template_id: customerId,
        ...values
      })
      setHTML(resHtml)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const onOpenHandler = async () => {
    try {
      const values = await form.validateFields()
      void fetchDocumentPreviewHandler({
        ...values,
        start_date: normalizeDate(values?.start_date),
        completion_date: normalizeDate(values?.completion_date)
      })
      setOpen(true)
    } catch (e) {
      const errorFields = (e as ValidateErrorEntity).errorFields

      if (errorFields?.length) {
        form.scrollToField(errorFields[0].name, {
          behavior: "smooth",
          block: "center",
          scrollMode: "always"
        })
      }
    }
  }

  const onSaveClickHandler = async () => {
    try {
      setAction("create")
      await onSubmitClick()
    } finally {
      setAction("")
    }
  }

  const onGenerateClickHandler = async () => {
    try {
      setAction("save")
      await onGenerateClick()
    } finally {
      setAction("")
    }
  }

  const onSendToApprovalClickHandler = async () => {
    try {
      setAction("approve")
      await onClickSendToApproval?.()
    } finally {
      setAction("")
    }
  }

  return (
    <>
      <Button
        disabled={saving}
        variant={"solid"}
        color={"primary"}
        icon={<EyeOutlined />}
        onClick={onOpenHandler}
      >
        Preview
      </Button>

      <Drawer
        open={open}
        width={"100vw"}
        title="Preview Document"
        closable={{ "aria-label": "Close Button" }}
        onClose={onCancelClick}
      >
        {/* The drawer body already pads 24px below the action row, so the gap
            above it has to match or the buttons sit high in that white band. */}
        <Flex vertical gap={"large"} className={"size-full"}>
          {loading ? (
            <Skeleton.Node active className={"size-full"} />
          ) : (
            <>
              {HTML ? (
                <iframe
                  srcDoc={HTML}
                  className="flex-1 border-0"
                  sandbox="allow-same-origin"
                  title="Preview"
                />
              ) : (
                <>
                  <Flex
                    flex={1}
                    justify={"center"}
                    align={"center"}
                    className={"flex 1"}
                  >
                    <Empty description={error ? "Error" : "No data"} />
                  </Flex>
                </>
              )}
            </>
          )}
          <Flex justify={"end"} className={"px-2"}>
            <Space>
              <Button
                disabled={!!action}
                loading={action === "save"}
                onClick={onGenerateClickHandler}
                type={onClickSendToApproval ? "default" : "primary"}
              >
                {alreadyHasFile ? "Regenerate" : "Generate"}
              </Button>
              <Button
                disabled={!!action}
                loading={action === "create"}
                onClick={onSaveClickHandler}
                type={onClickSendToApproval ? "default" : "primary"}
              >
                {submitLabel}
              </Button>
              {onClickSendToApproval && (
                <Button
                  disabled={!!action}
                  loading={action === "approve"}
                  onClick={onSendToApprovalClickHandler}
                  type={"primary"}
                >
                  Send to Approval
                </Button>
              )}
            </Space>
          </Flex>
        </Flex>
      </Drawer>
    </>
  )
}
